import React, { FC, useCallback, useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import Loader from "@components/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import AddPhotosDialog from "@components/AddPhotosDialog";
import { Album, AlbumType } from "@models/album";
import { useTranslation } from "react-i18next";
import {
  useAddAlbumPhotosMutation,
  useCreateAlbumMutation,
  useDeleteAlbumPhotosMutation,
  useGetAlbumPhotosQuery,
  useUpdateAlbumMutation,
} from "@store/albumApi";
import { useForm } from "react-hook-form";
import { Photo } from "@models/photo";
import { setMessage } from "@store/snackbarSlice";
import styled from "@emotion/styled";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TextIconButton from "@components/TextIconButton";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";
import Pictures from "@components/Pictures/Pictures";
import AlbumToolbar from "@components/Home/AlbumRoutes/EditAlbum/AlbumToolbar";
import CustomLabelInput from "@components/CustomLabelInput";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import EmptyState from "@components/EmptyState/EmptyState";

const Form = styled.form`
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

interface AlbumFormType {
  name: string;
}

const EditAlbum: FC<{ album?: Album; onSaved: () => void }> = ({
  album,
  onSaved,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [createAlbum] = useCreateAlbumMutation();
  const [updateAlbum] = useUpdateAlbumMutation();
  const [deletePhotos] = useDeleteAlbumPhotosMutation();
  const [addPhotos] = useAddAlbumPhotosMutation();
  const [checkedPhotos, setCheckedPhotos] = useState<Set<Photo>>(new Set());
  const [addedPhotos, setAddedPhotos] = useState<Photo[]>([]);
  const [coverPhoto, setCoverPhoto] = useState<Photo | undefined>();
  const [addPhotosDialogOpen, setAddPhotosDialogOpen] = useState(false);
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: photos, isLoading } = useGetAlbumPhotosQuery(
    {
      albumId: album?.id || "",
    },
    {
      skip: !album,
    },
  );

  const {
    register,
    clearErrors,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<AlbumFormType>();
  const resetForm = useCallback(() => {
    reset({
      name: "",
    });
    setCheckedPhotos(new Set());
  }, [reset]);

  useEffect(() => {
    if (album) {
      reset({ name: album.name });
      setCoverPhoto(album?.coverPhoto);
    }
  }, [album, reset]);

  const sendRequest = useCallback(
    async (fn: () => Promise<void>) => {
      try {
        await fn();
      } catch (e) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    },
    [dispatch, t],
  );

  const handleRequestResult = useCallback(
    (error?: FetchBaseQueryError | SerializedError) => {
      if (error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        dispatch(
          setMessage({
            content: t(`editAlbum.saveSuccessMessage`),
            severity: "success",
          }),
        );
      }
    },
    [dispatch, t],
  );

  const save = useCallback(
    async (values: AlbumFormType) => {
      await sendRequest(async () => {
        const photoIds = album ? [] : addedPhotos.map(({ id }) => id);
        const name = values.name;
        const { error } = await (album
          ? updateAlbum({
              id: album.id,
              name,
              coverPhotoId: coverPhoto?.id,
            })
          : createAlbum({
              name,
              photoIds,
              patientId: selectedPatientId!,
              albumType: AlbumType.Static,
              coverPhotoId: coverPhoto?.id ?? photoIds[0],
            }));

        handleRequestResult(error);
        if (!error) {
          setAddedPhotos([]);
          resetForm();
          onSaved();
        }
      });
    },
    [
      addedPhotos,
      selectedPatientId,
      createAlbum,
      onSaved,
      resetForm,
      album,
      updateAlbum,
      sendRequest,
      handleRequestResult,
      coverPhoto,
    ],
  );

  const onAddPhotos = useCallback(
    async (photos: Photo[]) => {
      if (!photos.length) {
        return;
      }
      if (!album) {
        setAddedPhotos(photos);
      } else {
        await sendRequest(async () => {
          const { error } = await addPhotos({
            albumId: album.id,
            photoIds: photos.map(({ id }) => id),
          });
          handleRequestResult(error);
        });
      }
      setAddPhotosDialogOpen(false);
    },
    [album, addPhotos, sendRequest, handleRequestResult],
  );

  const checkPhoto = useCallback((photo: Photo) => {
    setCheckedPhotos((photos: Set<Photo>) => {
      const newSet = new Set(photos);
      if (newSet.has(photo)) {
        newSet.delete(photo);
      } else {
        newSet.add(photo);
      }
      return newSet;
    });
  }, []);

  const removeCheckedPhotos = useCallback(async () => {
    if (!checkedPhotos.size) {
      return;
    }
    if (!album) {
      const removePhotoIds = Array.from(checkedPhotos).map(({ id }) => id);
      setAddedPhotos((photos) => {
        return photos.filter(({ id }) => !removePhotoIds.includes(id));
      });
    } else {
      await sendRequest(async () => {
        const { error } = await deletePhotos({
          albumId: album?.id,
          photoIds: Array.from(checkedPhotos).map(({ id }) => id),
        });

        handleRequestResult(error);

        if (!error) {
          setCheckedPhotos(new Set());
        }
      });
    }
  }, [checkedPhotos, album, deletePhotos, handleRequestResult, sendRequest]);

  const setAsCover = useCallback(async () => {
    if (checkedPhotos.size !== 1) {
      return;
    }
    const cp = Array.from(checkedPhotos)[0];
    if (cp) {
      setCoverPhoto(cp);
      setCheckedPhotos(new Set());
    }
  },[checkedPhotos]);

  return (
    <>
      <Box display="flex" flexDirection="column" flex="1">
        {isLoading || !selectedPatientId ? (
          <Loader />
        ) : (
          <>
            <Form
              onSubmit={(e) => {
                clearErrors();
                return handleSubmit(save)(e);
              }}
            >
              <Box maxWidth="328px" marginBottom="24px">
                <CustomLabelInput
                  label={t("editAlbum.nameInput.label")}
                  id="new-album-name"
                  required={true}
                >
                  {album?.allPhotos ? (
                      <Typography>
                        {t('albums.allPhotosAlbumLabel')}
                      </Typography>
                  ) : (
                    <TextField
                      {...register(`name`, {
                        required: true,
                      })}
                      error={!!errors?.name}
                      id="new-album-name"
                      type="text"
                      size="small"
                      variant="outlined"
                      disabled={album?.allPhotos ?? false}
                      fullWidth
                      placeholder={t("editAlbum.nameInput.placeholder")}
                    />
                  )}
                </CustomLabelInput>
              </Box>

              <Box marginBottom="32px">
                <AlbumToolbar
                  photoCount={album?.photoCount ?? addedPhotos.length ?? 0}
                  readOnly={album?.allPhotos ?? false}
                  counter={checkedPhotos.size}
                  onSelect={() => setAddPhotosDialogOpen(true)}
                  onRemove={removeCheckedPhotos}
                  onSetAsCover={setAsCover}
                />
              </Box>

              <Box flex="1">
                {!photos?.data?.length && !addedPhotos.length ? (
                  <EmptyState
                    image={`${process.env.PUBLIC_URL}/images/art-photos.jpeg`}
                    label={t("editAlbum.noData")}
                  />
                ) : (
                  <Pictures
                    pictures={photos?.data ? photos.data : addedPhotos}
                    onCheck={checkPhoto}
                    onSelect={checkPhoto}
                    checkedPictures={checkedPhotos}
                    coverPicture={coverPhoto}
                  />
                )}
              </Box>
              <Box marginTop="auto">
                <HomeFormButtons>
                  <TextIconButton
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    size="large"
                    icon={SaveOutlinedIcon}
                    text={t("editAlbum.saveButton")}
                  />
                </HomeFormButtons>
              </Box>
            </Form>
            <AddPhotosDialog
              patientId={selectedPatientId}
              albumId={album?.id}
              onAddPhotos={onAddPhotos}
              open={addPhotosDialogOpen}
              onClose={() => setAddPhotosDialogOpen(false)}
              excludePhotos={photos?.data || []}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default EditAlbum;
